import { gql, DocumentNode } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { addNegativeToast } from 'modules/toastContainer';
import { useGraphql } from 'utils/hooks/useGraphql';
import { isDefined } from 'utils/tsHelpers';
import { GraphQLSnippetUse } from '../types';

type Variables = {
  name: string;
};

export const fetchSnippetUsageByName = gql`
  query FindSnippetUsage($name: String!) {
    findSnippetUsage(name: $name) {
      creationDate
      creatorUser {
        id
        fullName
        avatarUrl
      }
      name
      id
      messageMedium
      campaignName
      campaignState
      campaignID
      updatedAt
      updatedByUser {
        id
        fullName
        avatarUrl
      }
      __typename
    }
  }
`;

const FAILURE_MESSAGE = `Search response took longer than expected. Please try again later.`;

export type UseSnippetSearchResponse = {
  results: GraphQLSnippetUse[];
  hasError?: boolean;
  isLoading: boolean;
};

/**
 * Raw response from GQL.
 */
type FetchSnippetQueryResponse = {
  findSnippetUsage: GraphQLSnippetUse[];
};

export function useSnippetSearch(
  variables: Variables = {
    name: '',
  },
  query: DocumentNode = fetchSnippetUsageByName,
): UseSnippetSearchResponse {
  const dispatch = useDispatch();
  const {
    data: snippetQueryData,
    error: hasError,
    isLoading,
  } = useGraphql(query, {
    variables,
    onError: () => dispatch(addNegativeToast(FAILURE_MESSAGE)),
  });

  // protect against the undefined response while the query is running
  if (isDefined<FetchSnippetQueryResponse>(snippetQueryData)) {
    const results = snippetQueryData.findSnippetUsage || [];

    return {
      hasError,
      isLoading,
      results,
    };
  }

  return {
    hasError: false,
    isLoading,
    results: [],
  };
}
